import type { CaptureLogOptions } from "posthog-js";

const REDACTED = "[redacted sensitive log payload]";
const SENSITIVE_KEY =
  /cookie|authorization|password|passwd|secret|token|api[_-]?key/i;
const SENSITIVE_ASSIGNMENT =
  /\b[\w.-]*(?:cookie|authorization|password|passwd|secret|token|api[_-]?key)[\w.-]*["']?\s*[:=]/i;

function sanitizeText(text: string): string {
  // Console serializers can nest JSON strings. Redact the whole payload rather
  // than guessing where an escaped cookie or credential value ends.
  if (SENSITIVE_ASSIGNMENT.test(text.replace(/\\+/g, ""))) return REDACTED;
  return text
    .replace(/\\\//g, "/")
    .replace(/https?:\/\/[^\s"'\\<>]+/gi, (url) =>
      url.split(/[?#]/, 1)[0].replace(/(https?:\/\/)[^/]*@/i, "$1"),
    );
}

function sanitizeValue(value: unknown, depth: number): unknown {
  if (depth > 6) return REDACTED;
  if (typeof value === "string") return sanitizeText(value);
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, depth + 1));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !SENSITIVE_KEY.test(key))
        .map(([key, item]) => [key, sanitizeValue(item, depth + 1)]),
    );
  }
  return value;
}

export function sanitizeConsoleLog(
  record: CaptureLogOptions,
): CaptureLogOptions {
  return {
    ...record,
    body: sanitizeText(record.body),
    attributes: {
      // The SDK adds the full browser URL *after* beforeSend. A null override
      // prevents it from restoring query strings that this boundary never saw.
      "url.full": null,
      ...Object.fromEntries(
        Object.entries(record.attributes ?? {})
          .filter(([key]) => !SENSITIVE_KEY.test(key))
          .map(([key, value]) => [key, sanitizeValue(value, 0)]),
      ),
    },
  };
}
